import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService, IClientAppConfig } from '@sondermind/configuration';
import { Appointment, IAppointmentApiResponse } from '@sondermind/data-access/appointments';
import { HttpService } from '@sondermind/http';
import { IAPIFilterOption, IAPIListRequestOptions, IAPIListResponse, IAPIPageOption } from '@sondermind/utilities/models-http';

export const FILTER_OPTIONS_PAST_SESSIONS: IAPIListRequestOptions = {
  filter: [
    { key: 'not_canceled', value: 'true' },
    { key: 'occurs_before', value: new Date().toISOString() }
  ],
  view: 'with_provider_info',
  sort: [
    { key: 'start_time', asc: false }
  ]
};

export const FILTER_OPTIONS_PAST_SESSIONS_INCLUDE_CANCELED: IAPIListRequestOptions = {
  filter: [
    { key: 'occurs_before', value: new Date().toISOString() }
  ],
  view: 'with_provider_info',
  sort: [
    { key: 'start_time', asc: false }
  ]
};

const appointmentPred = (res: IAPIListResponse<IAppointmentApiResponse>): IAPIListResponse<Appointment> => {
  const pastAppointments = res.data;
  // If an appointment is in-progress, remove it
  if (pastAppointments.length && new Date(res.data[0].end_time) > new Date()) {
    pastAppointments.shift();
  }

  return { ...res, data: pastAppointments.map((a) => new Appointment(a)) };
};

@Injectable({
  providedIn: 'root'
})
export class PastAppointmentsHttpService {
  constructor(
    private http: HttpService,
    private configurationService: ConfigurationService<IClientAppConfig>
  ) { }

  getClientPastAppointments$(): Observable<IAPIListResponse<Appointment>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events.json`;
    const params = this.http.generateListParams(FILTER_OPTIONS_PAST_SESSIONS);

    return this.http
      .get<IAPIListResponse<IAppointmentApiResponse>>(url, params)
      .pipe(
        map((res) => appointmentPred(res)),
        this.http.handleHttpError()
      );
  }

  getClientPastAppointmentsWithTherapist$(personaProviderId: number): Observable<IAPIListResponse<Appointment>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events.json`;

    // deep copy the filter options
    const filterOptionsPastSessions = JSON.parse(JSON.stringify(FILTER_OPTIONS_PAST_SESSIONS));
    filterOptionsPastSessions.filter.push({ key: 'persona_provider_id', value: personaProviderId });

    const params = this.http.generateListParams(filterOptionsPastSessions);

    return this.http
      .get<IAPIListResponse<IAppointmentApiResponse>>(url, params)
      .pipe(
        map((res) => appointmentPred(res)),
        this.http.handleHttpError()
      );
  }

  getClientPastAppointmentsIncludeCanceled$(): Observable<IAPIListResponse<Appointment>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events.json`;
    const params = this.http.generateListParams(FILTER_OPTIONS_PAST_SESSIONS_INCLUDE_CANCELED);

    return this.http
      .get<IAPIListResponse<IAppointmentApiResponse>>(url, params)
      .pipe(
        map((res) => appointmentPred(res)),
        this.http.handleHttpError()
      );
  }

  /**
   * Gets the most recent past session for a specific provider
   * Uses pagination to limit API response to 1 result for performance
   * Returns both the appointment data and count metadata
   */
  getLastSessionWithProvider$(personaProviderId: number): Observable<IAPIListResponse<Appointment>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events.json`;

    // deep copy the filter options
    const filterOptionsPastSessions: IAPIListRequestOptions = JSON.parse(JSON.stringify(FILTER_OPTIONS_PAST_SESSIONS));
    (filterOptionsPastSessions.filter as IAPIFilterOption[]).push(
      { key: 'persona_provider_id', value: personaProviderId.toString() }
    );
    const page: IAPIPageOption = { size: 1, number: 1 };
    filterOptionsPastSessions.page = page;

    const params = this.http.generateListParams(filterOptionsPastSessions);

    return this.http
      .get<IAPIListResponse<IAppointmentApiResponse>>(url, params)
      .pipe(
        map((res) => appointmentPred(res)),
        this.http.handleHttpError()
      );
  }
}
